import React, { useMemo, useState } from "react";
import { WaiterSharedProps, Page, TableModel } from "./types";
import { OrderProductStatusEnum } from "../../../enums/orders/order_status.enum";

interface Props extends WaiterSharedProps {
  initialSectionId: string | null;
}

type StatusFilter = "all" | "waiting" | "ready" | "occupied" | "empty";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Hepsi" },
  { key: "waiting", label: "⚡ Çağrı" },
  { key: "ready", label: "✅ Hazır" },
  { key: "occupied", label: "🟣 Sipariş" },
  { key: "empty", label: "🪑 Boş" },
];

const SECTION_COLORS = [
  "#F59E0B",
  "#22C55E",
  "#3B82F6",
  "#8B5CF6",
  "#F43F5E",
  "#14B8A6",
];

function tableStatus(
  table: TableModel,
  hasOrder: boolean,
  hasCall: boolean,
  hasReady: boolean,
): StatusFilter {
  if (hasCall) return "waiting";
  if (hasReady) return "ready";
  if (hasOrder) return "occupied";
  return "empty";
}

const STATUS_CFG: Record<
  StatusFilter,
  {
    label: string;
    border: string;
    shadow: string;
    badgeBg: string;
    badgeColor: string;
  }
> = {
  all: {
    label: "",
    border: "#E7E5E4",
    shadow: "none",
    badgeBg: "",
    badgeColor: "",
  },
  empty: {
    label: "Boş",
    border: "#E7E5E4",
    shadow: "none",
    badgeBg: "#F5F5F4",
    badgeColor: "#78716C",
  },
  occupied: {
    label: "Sipariş Var",
    border: "#A78BFA",
    shadow: "0 0 0 3px #EDE9FE",
    badgeBg: "#EDE9FE",
    badgeColor: "#7C3AED",
  },
  waiting: {
    label: "⚡ Çağrı!",
    border: "#F59E0B",
    shadow: "0 0 0 3px #FEF3C7",
    badgeBg: "#FEF3C7",
    badgeColor: "#92400E",
  },
  ready: {
    label: "✅ Hazır",
    border: "#34D399",
    shadow: "0 0 0 3px #D1FAE5",
    badgeBg: "#D1FAE5",
    badgeColor: "#065F46",
  },
};

const fmt = (n: number) => n.toLocaleString("tr-TR");

const WaiterTables: React.FC<Props> = ({
  sections,
  allTables,
  orderStateList,
  waiterCallList,
  initialSectionId,
  navigate,
}) => {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [activeSection, setActiveSection] = useState<string | null>(
    initialSectionId,
  );

  const currentSec = sections.find((s) => s.id === activeSection);

  // Her masa için hesaplanan durum
  const tableRows = useMemo(() => {
    return allTables.map((table) => {
      const orderState = orderStateList.find(
        (o) => o.order.tableId === table.id,
      );
      const calls = waiterCallList.filter((w) => w.tableId === table.id);
      const hasOrder = !!orderState;
      const hasCall = calls.length > 0;
      const hasReady = orderState
        ? orderState.orderProductList.some(
            (op) =>
              op.status.text !== OrderProductStatusEnum.pendingApproval &&
              op.status.text !== "served",
          )
        : false;
      const status = tableStatus(table, hasOrder, hasCall, hasReady);
      const total = orderState
        ? orderState.orderProductList.reduce(
            (s, op) => s + op.menuProductPrice * op.amount,
            0,
          )
        : 0;
      const preview = orderState?.orderProductList.slice(0, 2) ?? [];
      const extra = (orderState?.orderProductList.length ?? 0) - preview.length;
      return { table, orderState, calls, status, total, preview, extra };
    });
  }, [allTables, orderStateList, waiterCallList]);

  const displayed = tableRows.filter((row) => {
    const secOk =
      activeSection !== null ? row.table.sectionId === activeSection : true;
    const stOk = filter === "all" || row.status === filter;
    return secOk && stOk;
  });

  const countFor = (f: StatusFilter) =>
    tableRows.filter((row) => {
      const secOk =
        activeSection !== null ? row.table.sectionId === activeSection : true;
      return secOk && (f === "all" || row.status === f);
    }).length;

  return (
    <div
      style={{
        fontFamily: "'Nunito', sans-serif",
        background: "#FAFAF9",
        minHeight: "100%",
        paddingBottom: 24,
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1C1917",
          color: "#fff",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <button
          onClick={() => navigate({ name: "WaiterDashboard" })}
          style={{
            width: 36,
            height: 36,
            background: "#292524",
            border: "1px solid #44403C",
            borderRadius: 12,
            color: "#fff",
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: 14,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentSec ? currentSec.name : "Tüm Masalar"}
          </div>
          <div style={{ fontSize: 11, color: "#A8A29E", fontWeight: 600 }}>
            {displayed.length} masa ·{" "}
            {displayed.filter((r) => r.status !== "empty").length} dolu
          </div>
        </div>
      </div>

      {/* Bölüm sekmesi */}
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 16px",
          overflowX: "auto",
          background: "#fff",
          borderBottom: "1px solid #E7E5E4",
          position: "sticky",
          top: 60,
          zIndex: 40,
        }}
      >
        <button
          onClick={() => setActiveSection(null)}
          style={{
            padding: "6px 12px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 800,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
            background: activeSection === null ? "#1C1917" : "#F5F5F4",
            color: activeSection === null ? "#fff" : "#78716C",
          }}
        >
          Tümü
        </button>
        {sections.map((s, idx) => {
          const dotColor = SECTION_COLORS[idx % SECTION_COLORS.length];
          const sectionCallCount = waiterCallList.filter((w) =>
            allTables.find((t) => t.id === w.tableId && t.sectionId === s.id),
          ).length;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 800,
                border: "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 6,
                position: "relative",
                background: activeSection === s.id ? "#1C1917" : "#F5F5F4",
                color: activeSection === s.id ? "#fff" : "#78716C",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: dotColor,
                  flexShrink: 0,
                }}
              />
              {s.name}
              {sectionCallCount > 0 && (
                <span
                  style={{
                    background: "#EF4444",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "1px 5px",
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                >
                  {sectionCallCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Durum filtresi */}
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "8px 16px",
          overflowX: "auto",
          background: "#fff",
          borderBottom: "1px solid #F5F5F4",
          position: "sticky",
          top: 104,
          zIndex: 30,
        }}
      >
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: "5px 10px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 800,
              whiteSpace: "nowrap",
              cursor: "pointer",
              border: "1px solid",
              background: filter === f.key ? "#F59E0B" : "transparent",
              color: filter === f.key ? "#1C1917" : "#A8A29E",
              borderColor: filter === f.key ? "#F59E0B" : "#E7E5E4",
            }}
          >
            {f.label} ({countFor(f.key)})
          </button>
        ))}
      </div>

      {/* Masa kartları */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          padding: "12px 16px",
        }}
      >
        {displayed.length === 0 && (
          <div
            style={{
              gridColumn: "1/-1",
              textAlign: "center",
              padding: "60px 0",
              color: "#A8A29E",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Bu filtre için masa bulunamadı.
          </div>
        )}
        {displayed.map(
          ({ table, status, total, preview, extra, calls, orderState }) => {
            const cfg = STATUS_CFG[status];
            return (
              <button
                key={table.id}
                onClick={() =>
                  navigate({
                    name: "WaiterTableDetail",
                    tableId: table.id,
                    sectionId: table.sectionId,
                  })
                }
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  border: `1.5px solid ${cfg.border}`,
                  boxShadow: cfg.shadow,
                  padding: 13,
                  textAlign: "left",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                {/* Başlık */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{ fontWeight: 900, fontSize: 15, color: "#1C1917" }}
                  >
                    {table.name}
                  </span>
                  {status !== "empty" && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "3px 8px",
                        borderRadius: 20,
                        background: cfg.badgeBg,
                        color: cfg.badgeColor,
                      }}
                    >
                      {cfg.label}
                    </span>
                  )}
                </div>

                {/* Masa logosu veya boş ikon */}
                {status === "empty" ? (
                  <div style={{ textAlign: "center", padding: "10px 0" }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>🪑</div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#A8A29E",
                        fontWeight: 700,
                      }}
                    >
                      Müsait
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Sipariş özeti */}
                    <div style={{ marginBottom: 8 }}>
                      {preview.map((op) => (
                        <div
                          key={op.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#374151",
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginRight: 4,
                            }}
                          >
                            {op.menuProductName} ×{op.amount}
                          </span>
                          <span style={{ flexShrink: 0 }}>
                            ₺{fmt(op.menuProductPrice * op.amount)}
                          </span>
                        </div>
                      ))}
                      {extra > 0 && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "#A8A29E",
                            fontWeight: 600,
                          }}
                        >
                          +{extra} ürün daha…
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        paddingTop: 8,
                        borderTop: "1px dashed #E7E5E4",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#A8A29E",
                            fontWeight: 700,
                          }}
                        >
                          Toplam
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 900,
                            color: "#1C1917",
                          }}
                        >
                          ₺{fmt(total)}
                        </div>
                      </div>
                      {calls.length > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            background: "#FEF3C7",
                            color: "#92400E",
                            padding: "3px 8px",
                            borderRadius: 8,
                          }}
                        >
                          {calls.length} çağrı
                        </span>
                      )}
                    </div>
                  </>
                )}
              </button>
            );
          },
        )}
      </div>
    </div>
  );
};

export default WaiterTables;
